import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Checkbox,
  InputAdornment,
  ListItemText,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';

import { IFilterOption } from '../../../../../types/tables';
import {
  checkboxStyles,
  ClearIconWrapper,
  displayLabelStyles,
  FilterWrapper,
  getPaperPropsStyles,
  getSelectStyles,
  HoverOverlay,
  searchBoxWrapperStyles,
  searchTextFieldStyles,
  StyledClearIcon,
  StyledMenuItem,
  StyledSelect,
} from './FilterSelect.styles';

interface IProps {
  label: string;
  value: string | string[] | undefined | null;
  onChange: (value: string | string[] | undefined) => void;
  options: IFilterOption[];
  onClear: (onChange: (value: string | string[] | undefined) => void) => void;
  isMultiple?: boolean;
  limit?: number;
  onLimitReached?: () => void;
  dataAction?: string;
  disabled?: boolean;
  enableSearch?: boolean;
}

export const FilterSelect: React.FC<IProps> = ({
  label,
  value,
  onChange,
  options,
  onClear,
  isMultiple = false,
  limit,
  onLimitReached,
  dataAction,
  disabled = false,
  enableSearch = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const hasValue = useMemo(() => (isMultiple ? value && value.length > 0 : !!value), [isMultiple, value]);
  const selectValue = useMemo(() => {
    if (isMultiple) {
      return Array.isArray(value) ? value : [];
    }

    return value ?? '';
  }, [isMultiple, value]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const filteredOptions = useMemo(() => {
    if (!enableSearch || !searchQuery.trim()) {
      return options;
    }
    const query = searchQuery.toLowerCase().trim();
    return options.filter((option) => option.label.toLowerCase().includes(query));
  }, [enableSearch, searchQuery, options]);

  const renderDisplayLabel = useCallback(() => {
    if (isMultiple && value && value.length > 0 && Array.isArray(value)) {
      const selectedOptions = options.filter((opt) => value.includes(opt.value)).map((opt) => opt.label);
      return <Typography sx={displayLabelStyles}>{selectedOptions.join(', ')}</Typography>;
    }
    if (value) {
      const selectedOption = options.find((opt) => opt.value === value);
      return selectedOption?.label || label;
    }
    return label;
  }, [isMultiple, value, label, options]);

  const renderSearch = useCallback(() => {
    return (
      <Box sx={searchBoxWrapperStyles} onKeyDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
        <TextField
          placeholder="Search"
          value={searchQuery}
          onChange={handleSearchChange}
          fullWidth
          variant="outlined"
          size="small"
          autoFocus
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            },
          }}
          sx={searchTextFieldStyles}
        />
      </Box>
    );
  }, [searchQuery, handleSearchChange]);

  const renderNoOptionsItem = useCallback(() => {
    return (
      <MenuItem disabled>
        <ListItemText primary="No results found" />
      </MenuItem>
    );
  }, []);

  const handleChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const newValue = event.target.value;
      if (isMultiple && Array.isArray(newValue) && typeof limit === 'number') {
        if (newValue.length > limit) {
          onLimitReached?.();
          return;
        }
      }
      onChange(newValue as unknown as string | string[]);
      if (!isMultiple) {
        setOpen(false);
      }
    },
    [isMultiple, limit, onChange, onLimitReached],
  );

  const handleClearClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClear(onChange);
      setOpen(false);
    },
    [onClear, setOpen, onChange],
  );

  return (
    <FilterWrapper>
      <HoverOverlay className="hover-overlay" />
      <StyledSelect
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false);
          setSearchQuery('');
        }}
        multiple={isMultiple}
        value={selectValue}
        onChange={handleChange}
        displayEmpty
        renderValue={renderDisplayLabel}
        disableUnderline
        variant="standard"
        disabled={disabled}
        data-action={dataAction}
        MenuProps={{
          PaperProps: {
            sx: getPaperPropsStyles(enableSearch),
          },
        }}
        sx={getSelectStyles(!!hasValue)}
      >
        {enableSearch && renderSearch()}
        {filteredOptions.length === 0
          ? renderNoOptionsItem()
          : filteredOptions.map((option) => {
              const { value: optionValue, label: optionLabel } = option;
              const isValueSelected = Array.isArray(value) && value && value.includes(optionValue);

              return (
                <StyledMenuItem
                  key={optionValue}
                  value={optionValue}
                  selected={isMultiple ? isValueSelected : value === optionValue}
                >
                  {isMultiple && <Checkbox sx={checkboxStyles} checked={isValueSelected} />}
                  <ListItemText primary={optionLabel} />
                </StyledMenuItem>
              );
            })}
      </StyledSelect>
      {hasValue && (
        <ClearIconWrapper onClick={handleClearClick}>
          <StyledClearIcon />
        </ClearIconWrapper>
      )}
    </FilterWrapper>
  );
};
